// src/redux/slices/admin/transport-bookings-slice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { transportBookingsService } from "@/services/system-admin/transport-bookings.service";
import type {
  TransportBookingsState,
  GetTransportBookingsParams,
  ConfirmBookingRequest,
  RejectBookingRequest,
  RequestBookingInfoRequest,
  AssignVehicleRequest,
  AssignPartnerRequest,
  CancelBookingRequest,
  CommentOnlyRequest,
  CreateQuoteRequest,
} from "@/types/admin/transport-booking-type";

const initialState: TransportBookingsState = {
  bookings: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,

  bookingDetail: null,
  quotes: [],
  quoteItems: [],
  messages: [],
  detailLoading: false,

  history: [],
  historyLoading: false,

  actionLoading: false,
};

export const fetchTransportBookings = createAsyncThunk(
  "transportBookings/fetchTransportBookings",
  async (params: GetTransportBookingsParams) => {
    const response = await transportBookingsService.getBookings(params);
    return {
      ...response,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
  },
);

export const fetchTransportBookingDetail = createAsyncThunk(
  "transportBookings/fetchTransportBookingDetail",
  async (bookingUid: string) =>
    transportBookingsService.getBookingById(bookingUid),
);

export const fetchTransportBookingHistory = createAsyncThunk(
  "transportBookings/fetchTransportBookingHistory",
  async (bookingUid: string) =>
    transportBookingsService.getBookingHistory(bookingUid),
);

export const confirmTransportBooking = createAsyncThunk(
  "transportBookings/confirmTransportBooking",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: ConfirmBookingRequest;
  }) => transportBookingsService.confirmBooking(bookingUid, payload),
);

export const rejectTransportBooking = createAsyncThunk(
  "transportBookings/rejectTransportBooking",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: RejectBookingRequest;
  }) => transportBookingsService.rejectBooking(bookingUid, payload),
);

export const requestTransportBookingInfo = createAsyncThunk(
  "transportBookings/requestTransportBookingInfo",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: RequestBookingInfoRequest;
  }) => transportBookingsService.requestInformation(bookingUid, payload),
);

export const assignTransportVehicle = createAsyncThunk(
  "transportBookings/assignTransportVehicle",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: AssignVehicleRequest;
  }) => transportBookingsService.assignVehicle(bookingUid, payload),
);

export const assignTransportPartner = createAsyncThunk(
  "transportBookings/assignTransportPartner",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: AssignPartnerRequest;
  }) => transportBookingsService.assignPartner(bookingUid, payload),
);

export const cancelTransportBooking = createAsyncThunk(
  "transportBookings/cancelTransportBooking",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: CancelBookingRequest;
  }) => transportBookingsService.cancelBooking(bookingUid, payload),
);

export const startTransportBooking = createAsyncThunk(
  "transportBookings/startTransportBooking",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: CommentOnlyRequest;
  }) => transportBookingsService.startBooking(bookingUid, payload),
);

export const completeTransportBooking = createAsyncThunk(
  "transportBookings/completeTransportBooking",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: CommentOnlyRequest;
  }) => transportBookingsService.completeBooking(bookingUid, payload),
);

export const createTransportBookingQuote = createAsyncThunk(
  "transportBookings/createTransportBookingQuote",
  async ({
    bookingUid,
    payload,
  }: {
    bookingUid: string;
    payload: CreateQuoteRequest;
  }) => transportBookingsService.createQuote(bookingUid, payload),
);

const transportBookingsSlice = createSlice({
  name: "transportBookings",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearBookingDetail: (state) => {
      state.bookingDetail = null;
      state.history = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(fetchTransportBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransportBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchTransportBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch bookings.";
      })

      // ---- detail ----
      .addCase(fetchTransportBookingDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
   .addCase(fetchTransportBookingDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.bookingDetail = action.payload.booking;
        state.quotes = action.payload.quotes;
        state.quoteItems = action.payload.quoteItems;
        state.history = action.payload.history;
        state.messages = action.payload.messages;
      })
      .addCase(fetchTransportBookingDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || "Failed to fetch booking.";
      })

      // ---- history ----
      .addCase(fetchTransportBookingHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchTransportBookingHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = action.payload;
      })
      .addCase(fetchTransportBookingHistory.rejected, (state) => {
        state.historyLoading = false;
      })

      // ---- generic action matchers (confirm/reject/request-info/assign/cancel/start/complete) ----
      .addMatcher(
        (action) =>
          action.type.startsWith("transportBookings/") &&
          action.type.endsWith("/pending") &&
          !action.type.includes("fetchTransportBookings") &&
          !action.type.includes("fetchTransportBookingDetail") &&
          !action.type.includes("fetchTransportBookingHistory"),
        (state) => {
          state.actionLoading = true;
          state.error = null;
        },
      )
   .addMatcher(
        (action) =>
          action.type.startsWith("transportBookings/") &&
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("fetchTransportBookings") &&
          !action.type.includes("fetchTransportBookingDetail") &&
          !action.type.includes("fetchTransportBookingHistory"),
        (state, action: PayloadAction<any>) => {
          state.actionLoading = false;
          const payload = action.payload;
          if (payload?.booking) {
            state.bookingDetail = payload.booking;
            state.quotes = payload.quotes ?? state.quotes;
            state.quoteItems = payload.quoteItems ?? state.quoteItems;
            state.history = payload.history ?? state.history;
            state.messages = payload.messages ?? state.messages;
          } else if (payload) {
            state.bookingDetail = payload;
          }
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("transportBookings/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("fetchTransportBookings") &&
          !action.type.includes("fetchTransportBookingDetail") &&
          !action.type.includes("fetchTransportBookingHistory"),
        (state, action: any) => {
          state.actionLoading = false;
          state.error = action.error?.message || "Action failed.";
        },
      );
  },
});

export const { setPage, setPageSize, clearBookingDetail, clearError } =
  transportBookingsSlice.actions;
export default transportBookingsSlice.reducer;